import React, { useCallback, useState } from 'react';
import Plot from 'react-plotly.js';
import axios from '../../axiosConfig';

/**
 * AI 기반 지표면 응답스펙트럼 예측 패널.
 *
 * 지반응답해석이 등가선형 이론으로 지표면 응답을 계산하는 것과 별개로,
 * 딥러닝 모델이 지층 구성과 지중 응답스펙트럼으로부터 주기별 지반증폭인자를
 * 직접 예측한다. 두 결과를 함께 놓고 볼 수 있도록 상세 화면 안에 배치한다.
 *
 * 예측 모듈은 별도 실행 환경을 갖는 독립 프로세스이므로 첫 호출에 시간이 걸린다.
 * 사용자가 필요할 때만 실행하도록 버튼으로 분리하였다.
 */
const PRIMARY = '#2563eb';
const GRAY = '#6b7280';
const RED = '#dc2626';

const BASE_LAYOUT = {
  margin: { l: 56, r: 16, t: 12, b: 44 },
  font: { family: 'Pretendard, -apple-system, sans-serif', size: 11, color: '#374151' },
  paper_bgcolor: 'white',
  plot_bgcolor: 'white',
  hovermode: 'closest',
};
const AXIS = {
  gridcolor: '#f3f4f6',
  zerolinecolor: '#e5e7eb',
  linecolor: '#e5e7eb',
  ticks: 'outside',
  tickcolor: '#e5e7eb',
};
const LOG_TICKS = { tickmode: 'array', tickvals: [0.01, 0.1, 1, 10], ticktext: ['0.01', '0.1', '1', '10'] };
const CONFIG = { displayModeBar: false, responsive: true };

const ResponseSpectrumPanel = ({ boreholeId, earthquakeIndex, materialSet, available }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const predict = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/seismic/response-spectrum', {
        borehole_id: boreholeId,
        earthquake_index: earthquakeIndex,
        material_set: materialSet || null,
      });
      setResult(res.data.data);
    } catch (e) {
      const detail = e?.response?.data?.message || e?.response?.data?.detail;
      setError(detail || '응답스펙트럼 예측에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [boreholeId, earthquakeIndex, materialSet]);

  if (!available) {
    return (
      <section className="border border-gray-200 rounded-lg">
        <div className="px-4 py-2.5 border-b border-gray-200">
          <span className="text-sm font-semibold text-gray-700">AI 응답스펙트럼 예측</span>
        </div>
        <div className="p-6 text-sm text-gray-400 text-center">
          예측 모듈 실행 환경이 준비되지 않아 사용할 수 없습니다.
        </div>
      </section>
    );
  }

  return (
    <section className="border border-gray-200 rounded-lg">
      <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
        <span className="text-sm font-semibold text-gray-700">AI 응답스펙트럼 예측</span>
        <button
          onClick={predict}
          disabled={loading}
          className="px-3 py-1.5 rounded bg-primary-600 text-white text-xs font-semibold
                     hover:bg-primary-700 disabled:bg-gray-300"
        >
          {loading ? '예측 중…' : result ? '다시 예측' : '예측 실행'}
        </button>
      </div>

      <div className="p-4">
        {error && (
          <div className="px-4 py-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {!result && !error && (
          <p className="py-8 text-center text-sm text-gray-400">
            딥러닝 모델이 지층 구성과 지중 응답스펙트럼으로부터 주기별 지반증폭인자를 예측합니다.
            <br />첫 실행은 모델 적재로 시간이 걸릴 수 있습니다.
          </p>
        )}

        {result && (
          <>
            {result.extrapolation?.out_of_range && (
              <div className="mb-3 px-4 py-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <strong>외삽 경고</strong> — 이 부지의 특성이 모델 학습 분포를 벗어납니다.
                <ul className="mt-1 list-disc list-inside">
                  {result.extrapolation.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">응답스펙트럼 (지중 입력 대비 지표면 예측)</div>
                <Plot
                  data={[
                    {
                      x: result.periods, y: result.borehole_psa,
                      type: 'scatter', mode: 'lines', name: '지중 입력',
                      line: { color: GRAY, width: 1.6 },
                    },
                    {
                      x: result.periods, y: result.surface_psa,
                      type: 'scatter', mode: 'lines', name: '지표면 예측',
                      line: { color: PRIMARY, width: 2 },
                    },
                  ]}
                  layout={{
                    ...BASE_LAYOUT, height: 260, showlegend: true,
                    legend: { orientation: 'h', x: 1, xanchor: 'right', y: 1.16, font: { size: 10 } },
                    xaxis: { ...AXIS, ...LOG_TICKS, type: 'log', title: { text: '주기 T (s)', font: { size: 10 } } },
                    yaxis: { ...AXIS, title: { text: 'PSA (m/s²)', font: { size: 10 } } },
                  }}
                  config={CONFIG} style={{ width: '100%' }} useResizeHandler
                />
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">예측 지반증폭인자</div>
                <Plot
                  data={[
                    {
                      x: result.periods, y: result.amplification,
                      type: 'scatter', mode: 'lines',
                      line: { color: RED, width: 2 },
                      hovertemplate: 'T %{x:.3f} s<br>증폭 %{y:.3f}<extra></extra>',
                    },
                  ]}
                  layout={{
                    ...BASE_LAYOUT, height: 260, showlegend: false,
                    xaxis: { ...AXIS, ...LOG_TICKS, type: 'log', title: { text: '주기 T (s)', font: { size: 10 } } },
                    yaxis: { ...AXIS, title: { text: '증폭인자', font: { size: 10 } } },
                    shapes: [{
                      type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 1, y1: 1,
                      line: { color: '#9ca3af', width: 1, dash: 'dash' },
                    }],
                  }}
                  config={CONFIG} style={{ width: '100%' }} useResizeHandler
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              <Stat label="Vs30" value={`${result.site.Vs30} m/s`} />
              <Stat label="부지 고유주기" value={`${result.site.T_site} s`} />
              <Stat label="부지 분류" value={`class ${result.site.site_class}`} />
              <Stat label="최대 증폭인자" value={`${result.peak.amp_max} ×`} />
              <Stat label="지중 PSA 최대" value={`${result.peak.borehole_psa_max} m/s²`} />
              <Stat label="지표면 PSA 최대" value={`${result.peak.surface_psa_max} m/s²`} />
              <Stat label="최대 응답 주기" value={`${result.peak.amp_at_peak_period} s`} />
              <Stat label="평균 Vs" value={`${result.site.avg_vs} m/s`} />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div className="border border-gray-200 rounded px-3 py-2">
    <div className="text-[11px] text-gray-500">{label}</div>
    <div className="text-sm font-semibold text-gray-800">{value}</div>
  </div>
);

export default ResponseSpectrumPanel;
